import './AttentionTile.css';

export type AttentionSeverity = 'neutral' | 'good' | 'warning' | 'serious' | 'critical';

const SEVERITY_WORD: Record<AttentionSeverity, string> = {
  neutral: 'Note',
  good: 'Good',
  warning: 'Warning',
  serious: 'Serious',
  critical: 'Critical',
};

export interface AttentionTileProps {
  severity: AttentionSeverity;
  /** A short glyph/character, e.g. "$" — a domain icon, not a severity
   *  signal on its own. */
  icon: string;
  title: string;
  sub?: string;
  /** Overrides the corner word. Defaults to the severity's own name — the
   *  accessibility contract this primitive exists for ("never colour
   *  alone") is that word, not the stripe. See ../screens/Today.tsx for
   *  the one deliberate override that matches this component to the
   *  blessed pixel reference's "Next" label instead. */
  word?: string;
  onClick?: () => void;
}

// Trust-posture primitive (5) — severity is always stripe + icon + WORD
// together, never colour alone.
export function AttentionTile({ severity, icon, title, sub, word, onClick }: AttentionTileProps) {
  const interactive = onClick
    ? {
        role: 'button' as const,
        tabIndex: 0,
        onClick,
        onKeyDown: (e: KeyboardEvent) => (e.key === 'Enter' || e.key === ' ') && onClick(),
      }
    : {};
  return (
    <div class={`attention-tile attention-tile--${severity}`} {...interactive}>
      <div class="attention-tile__icon">{icon}</div>
      <div class="attention-tile__body">
        <div class="attention-tile__title">{title}</div>
        {sub ? <div class="attention-tile__sub">{sub}</div> : null}
      </div>
      <div class="attention-tile__word">{word ?? SEVERITY_WORD[severity]}</div>
    </div>
  );
}
